import React, { useMemo } from 'react';
import NTDrawColumn from '../NTLayout/NTDrawColumn';
import NTDrawHeader from '../NTLayout/NTDrawHeader';
import { prefixClass } from 'lib/utils';
import { ReportColumn, ReportViewModel } from 'types/report';
import { getTotalColumn, getDifferenceColumn } from '@hone-automation/common';
const prefix = prefixClass('nt-layout');

const useColumnRenderer = (
  data: ReportViewModel,
  columnsData: ReportColumn[],
  addTotalRow: boolean,
  addTotalColumn: boolean,
  addDifference: boolean,
  liveReport: number,
  fixFC: boolean,
  fixLC: boolean,
  isReversed: boolean = false,
  containerHeight: number = 600 // Height for virtualization
) => {
  // Memoize the rendered columns to prevent re-creating JSX on every render
  return useMemo(() => {
    let localColumns: ReportColumn[] = columnsData ? [...columnsData] : [];

    // Add utility columns if needed
    const totalColumn = getTotalColumn(data);
    const differenceColumn = getDifferenceColumn(data);

    if (addTotalColumn && !addDifference) {
      if (totalColumn) {
        localColumns.push(totalColumn);
      }
    } else if (addDifference && !addTotalColumn) {
      if (differenceColumn) {
        localColumns.push(differenceColumn);
      }
    }

    // When fixLC is true, ensure total/difference columns are moved to the end
    if (fixLC && localColumns.length > 0) {
      const totalColumns = localColumns.filter(
        col =>
          col.total ||
          col.originType === 'Total' ||
          col.originType === 'Synthetic Total' ||
          col.originType === 'Difference' ||
          col.originType === 'Synthetic Difference'
      );
      const regularColumns = localColumns.filter(
        col =>
          !col.total &&
          col.originType !== 'Total' &&
          col.originType !== 'Synthetic Total' &&
          col.originType !== 'Difference' &&
          col.originType !== 'Synthetic Difference'
      );

      // Reorder: regular columns first, then total/difference columns at the end
      if (totalColumns.length > 0) {
        localColumns = [...regularColumns, ...totalColumns];
      }
    }

    const renderedColumns: any[] = [];
    const renderedHeaders: any[] = [];
    const columns: number = localColumns.length;
    // Calculate lastColumn after reordering to ensure correct index
    const lastColumn: number = columns - 1;
    let middleProcessed: boolean = false;
    const middleColumnsStart: number = fixFC ? 1 : 0;
    const middleColumnsEnd: number = fixLC ? columns - 1 : columns;

    localColumns.forEach((mainColumn: ReportColumn, columnIndex: number | undefined) => {
      const fixed = columns > 2 && ((fixFC && columnIndex === 0) || (fixLC && columnIndex === lastColumn));
      const isDifferenceColumn =
        mainColumn.name === 'Difference' ||
        mainColumn.originType === 'Difference' ||
        mainColumn.originType === 'Synthetic Difference';
      if ((fixFC && columnIndex === 0) || (columnIndex === lastColumn && fixLC)) {
        renderedHeaders.push(
          <NTDrawHeader
            key={`header${columnIndex}`}
            columnIndex={columnIndex}
            data={mainColumn}
            fixed={fixed}
            isDifferenceColumn={isDifferenceColumn}
          />
        );
        renderedColumns.push(
          <NTDrawColumn
            key={`column${columnIndex}`}
            columnIndex={columnIndex}
            title={mainColumn.name}
            columnData={mainColumn.data}
            fixed={fixed}
            totalColumn={mainColumn.total}
            isDifferenceColumn={isDifferenceColumn}
          />
        );
      } else if (!middleProcessed) {
        // Backend handles column reversal - data already in correct order
        const middleColumns = localColumns.slice(middleColumnsStart, middleColumnsEnd);

        renderedHeaders.push(
          <div
            id="header-middle"
            key={`header${middleColumnsStart}-${middleColumnsEnd}-${isReversed ? 'rev' : 'norm'}`}
            className={`${prefix('column')} scrollable`}
          >
            {middleColumns.map((column: any, columnIndex: number) => (
              <NTDrawHeader
                key={`middleHeader-${column.name}-${columnIndex}-${isReversed ? 'rev' : 'norm'}`}
                columnIndex={columnIndex + middleColumnsStart}
                data={localColumns[columnIndex + middleColumnsStart]}
                isDifferenceColumn={
                  column.name === 'Difference' ||
                  column.originType === 'Difference' ||
                  column.originType === 'Synthetic Difference'
                }
              />
            ))}
          </div>
        );
        renderedColumns.push(
          <div
            id="table-middle"
            key={`column${middleColumnsStart}-${middleColumnsEnd}-${isReversed ? 'rev' : 'norm'}`}
            className={`${prefix('column')} scrollable`}
          >
            {middleColumns.map((column: any, columnIndex: number) => (
              <NTDrawColumn
                key={`middle-${column.name}-${columnIndex}-${isReversed ? 'rev' : 'norm'}`}
                liveColumn={column.liveReport}
                columnIndex={columnIndex + middleColumnsStart}
                title={column.name}
                columnData={column.data}
                totalColumn={column.total}
                compoundColumn={column.compoundColumn}
                isDifferenceColumn={
                  column.name === 'Difference' ||
                  column.originType === 'Difference' ||
                  column.originType === 'Synthetic Difference'
                }
              />
            ))}
          </div>
        );
        middleProcessed = true;
      }
    });

    return { renderedHeaders, renderedColumns };
  }, [
    data,
    columnsData,
    addTotalRow,
    addTotalColumn,
    addDifference,
    liveReport,
    fixFC,
    fixLC,
    isReversed,
    containerHeight,
  ]);
};

export default useColumnRenderer;
